'use client';

import React, { useEffect, useRef } from 'react';

const archLayers = [
  {
    label: 'Source Systems',
    color: '#F5F4F0',
    borderColor: '#C8C7C3',
    nodes: ['S/4HANA (Finance)', 'S/4HANA (Sales)', 'S/4HANA (MM/PP)', 'Non-SAP ERP', 'External Data Sources'],
  },
  {
    label: 'Data Foundation Layer',
    color: '#EEF4FF',
    borderColor: '#B8D0F8',
    nodes: ['SAP Datasphere — Business Layer', 'SAP Datasphere — Replication Layer', 'Data Quality & Validation'],
  },
  {
    label: 'SAC Planning Models',
    color: '#F0FFF4',
    borderColor: '#A8E6BC',
    nodes: ['Financial Planning Model', 'Sales Planning Model', 'Supply Chain Model', 'Driver / Allocation Models'],
  },
  {
    label: 'Planning Logic Engine',
    color: '#FFFBF0',
    borderColor: '#F0D890',
    nodes: ['Data Actions', 'Advanced Formula Scripts', 'Allocation Engine', 'Scenario Logic'],
  },
  {
    label: 'Planning Experience',
    color: '#FFF0F8',
    borderColor: '#F0B8D8',
    nodes: ['Planning Stories', 'Input Forms', 'Variance Dashboards', 'Workflow Tasks'],
  },
  {
    label: 'Governance & Security',
    color: '#F5F4F0',
    borderColor: '#C8C7C3',
    nodes: ['Role-Based Access', 'Version Management', 'Audit Trail', 'Planning Calendars'],
  },
];

export default function SACArchitectureDiagram() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.03 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 border-b border-border">
      <div className="max-w-container mx-auto">
        <div className="mb-10 reveal-on-scroll">
          <div className="mono-label mb-3">Reference Architecture</div>
          <h2
            className="font-heading font-semibold text-foreground"
            style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.25rem)', letterSpacing: '-0.025em' }}
          >
            SAC Planning Architecture Layers
          </h2>
          <p className="mt-2 text-sm text-muted font-body max-w-lg leading-relaxed">
            The six architectural layers of a SAC Planning deployment — from source systems through to planning governance.
          </p>
        </div>

        {/* Architecture diagram */}
        <div className="flex flex-col gap-2 reveal-on-scroll">
          {archLayers.map((layer, i) => (
            <div
              key={layer.label}
              className="arch-node border rounded p-4 transition-all duration-200"
              style={{
                backgroundColor: layer.color,
                borderColor: layer.borderColor,
                borderRadius: '4px',
              }}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="md:w-56 shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="mono-label">{String(i + 1).padStart(2, '0')}</span>
                    <span className="font-heading font-semibold text-foreground text-sm">
                      {layer.label}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {layer.nodes.map((node) => (
                    <span
                      key={node}
                      className="font-mono text-xs px-2.5 py-1 bg-white border rounded"
                      style={{ borderColor: layer.borderColor, borderRadius: '3px', fontSize: '11px' }}
                    >
                      {node}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Arrow indicator */}
        <div className="mt-4 flex items-center gap-2 reveal-on-scroll reveal-delay-2">
          <div className="flex flex-col items-center gap-1">
            <div className="w-px h-6 bg-border-strong" />
            <span className="text-muted" style={{ fontSize: '10px' }}>▼</span>
          </div>
          <span className="mono-label">Data flows from source systems downward through each layer</span>
        </div>
      </div>
    </section>
  );
}